import { useState } from "react";
import { Copy, Loader2, Pencil, Presentation, Rocket } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, downloadFile, formatErr } from "@/lib/api";

const outlineText = (w) => [w.title, w.subtitle, "", ...w.outline.flatMap((s) => [`${s.section} (${s.minutes} min)`, ...s.points.map((p) => `  - ${p}`)])].join("\n");

export function OutlineView({ w }) {
  const [open, setOpen] = useState(false);
  if (!w.outline?.length) return null;
  return (
    <div data-testid={`workshop-outline-${w.workshop_id}`}>
      <button onClick={() => setOpen(!open)} className="text-xs text-emerald-700 hover:underline" data-testid={`workshop-outline-toggle-${w.workshop_id}`}>{open ? "Hide" : "Show"} talk outline ({w.outline.reduce((a, s) => a + s.minutes, 0)} min)</button>
      {open && (
        <div className="mt-2 bg-slate-50 border border-slate-100 rounded p-3 text-sm">
          <ol className="space-y-2">{w.outline.map((s, i) => <li key={i}><b>{s.section}</b> <span className="text-slate-500">· {s.minutes} min</span><ul className="list-disc pl-5 text-slate-600">{s.points.map((p, j) => <li key={j}>{p}</li>)}</ul></li>)}</ol>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => navigator.clipboard.writeText(outlineText(w)).then(() => toast.success("Outline copied"))} data-testid={`workshop-outline-copy-${w.workshop_id}`}><Copy className="h-4 w-4 mr-1" />Copy outline</Button>
        </div>
      )}
    </div>
  );
}

export function EditDetails({ w, onClose, onSaved }) {
  const [f, setF] = useState({ library: w.library, date: w.date, start: w.start || "10:00", end: w.end || "11:00", address: w.address || "", lang: w.lang, capacity: w.capacity || 0,
    title: w.title || "", subtitle: w.subtitle || "", bullets: (w.bullets || []).join("\n") });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/workshops/${w.workshop_id}/details`, { ...f, capacity: Number(f.capacity) || 0, bullets: f.bullets.split("\n").filter((b) => b.trim()).slice(0, 5) }); toast.success("Workshop updated"); onSaved(); onClose(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto" data-testid="workshop-edit-dialog">
        <DialogHeader><DialogTitle>Edit workshop</DialogTitle></DialogHeader>
        <Input placeholder="Library" value={f.library} onChange={set("library")} data-testid="workshop-edit-library" />
        <div className="grid grid-cols-3 gap-2"><Input type="date" value={f.date} onChange={set("date")} className="col-span-3 sm:col-span-1" data-testid="workshop-edit-date" />
          <Input type="time" value={f.start} onChange={set("start")} data-testid="workshop-edit-start" /><Input type="time" value={f.end} onChange={set("end")} data-testid="workshop-edit-end" /></div>
        <Input placeholder="Address and room" value={f.address} onChange={set("address")} data-testid="workshop-edit-address" />
        <div className="grid grid-cols-2 gap-2">
          <select value={f.lang} onChange={set("lang")} className="h-10 border border-slate-300 rounded-md px-2 text-sm bg-white" data-testid="workshop-edit-lang"><option value="en">English</option><option value="es">Spanish</option></select>
          <Input placeholder="Seat limit (blank = no limit)" value={f.capacity || ""} onChange={(e) => setF({ ...f, capacity: e.target.value.replace(/\D/g, "") })} data-testid="workshop-edit-capacity" /></div>
        <Input placeholder="Flyer headline" value={f.title} maxLength={70} onChange={set("title")} data-testid="workshop-edit-title" />
        <Input placeholder="One-line description" value={f.subtitle} maxLength={160} onChange={set("subtitle")} data-testid="workshop-edit-subtitle" />
        <label className="text-xs text-slate-500">"What you'll learn" points (one per line, up to 5)</label>
        <Textarea rows={5} value={f.bullets} onChange={set("bullets")} data-testid="workshop-edit-bullets" />
        <Button onClick={save} disabled={busy || f.library.length < 2 || !f.date} className="bg-emerald-600 hover:bg-emerald-700" data-testid="workshop-edit-save">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save</Button>
      </DialogContent>
    </Dialog>
  );
}

export function DraftActions({ w, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [slides, setSlides] = useState(null);
  const dl = async (fmt) => {
    setSlides(fmt);
    try { await downloadFile(`/admin/workshops/${w.workshop_id}/slides.${fmt}`, `GrantVerify-Workshop-Slides-${w.date}.${fmt}`); }
    catch { toast.error("Couldn't build the slides right now. Please try again."); } finally { setSlides(null); }
  };
  const publish = () => api.post(`/admin/workshops/${w.workshop_id}/publish`).then(() => { toast.success("Published. The sign-up page and library page are live."); onSaved(); }).catch((e) => toast.error(formatErr(e)));
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid={`workshop-edit-${w.workshop_id}`}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
      {w.outline?.length > 0 && [["pptx", "Slides (PowerPoint)"], ["pdf", "Slides (PDF)"]].map(([fmt, label]) => (
        <Button key={fmt} size="sm" variant="outline" onClick={() => dl(fmt)} disabled={!!slides} title="Includes speaker notes" data-testid={`workshop-slides-${fmt}-${w.workshop_id}`}>
          {slides === fmt ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Presentation className="h-4 w-4 mr-1" />}{label}</Button>))}
      {w.status === "draft" && <Button size="sm" onClick={publish} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`workshop-publish-${w.workshop_id}`}><Rocket className="h-4 w-4 mr-1" />Publish</Button>}
      {edit && <EditDetails w={w} onClose={() => setEdit(false)} onSaved={onSaved} />}
    </>
  );
}
